import { ServerStatus, type Server } from "@cloudserver/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ExternalLink,
  MonitorPlay,
  Plus,
  Power,
  PowerOff,
  RotateCcw,
  Server as ServerIcon,
  SquareTerminal,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppliances } from "../api/packages.js";
import { useCanWrite, useReadOnly } from "../api/users.js";
import {
  runningByServer,
  useRunningOperations,
  useServerAction,
  useServers,
} from "../api/servers.js";
import { ErrorMessage, problemText } from "../ui/feedback.js";
import { relativeTime } from "../ui/format.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import type { MenuItem } from "../ui/overlay.js";
import { Alert, Badge, Button, Select } from "../ui/primitives.js";
import { OsIcon } from "../ui/os-icon.js";
import { DataTable } from "../ui/table.js";
import { serverBlock, terminals } from "../terminal/store.js";
import { useToast } from "../ui/toast.js";
import { ApiProblem } from "../api/client.js";
import { PowerDialog, type DisruptiveAction } from "./server/power-dialog.js";
import { operationText, specLine, StatusLabel } from "./server/status.js";
import { useOperationToasts } from "./server/watch.js";

/** Link to the create-server page. */
export function CreateServerButton({
  projectId,
  disabled,
}: {
  projectId: string;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  if (disabled) {
    return (
      <Button disabled icon={<Plus className="size-4" aria-hidden />}>
        {t("servers.create")}
      </Button>
    );
  }
  return (
    <Link
      to="/projects/$projectId/servers/new"
      params={{ projectId }}
      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-accent px-4 text-sm font-medium text-accent-fg hover:bg-accent-strong"
    >
      <Plus className="size-4" aria-hidden />
      {t("servers.create")}
    </Link>
  );
}

/** Name/IP text and status filter; empty values match everything. */
export function matchesFilter(server: Server, text: string, status: string): boolean {
  if (status && server.status !== status) return false;
  const needle = text.trim().toLowerCase();
  if (!needle) return true;
  return [
    server.name,
    server.publicIpv4?.address,
    server.publicIpv6?.network,
    ...server.privateIps.map((p) => p.ip),
  ].some((value) => value?.toLowerCase().includes(needle));
}

/** A row's power action: power-on runs at once, the others ask first. */
function useRowPowerOn(projectId: string) {
  const { t } = useTranslation();
  const toast = useToast();
  const run = useServerAction(projectId);
  return (server: Server, onStarted: (id: string) => void) => {
    run.mutate(
      { serverId: server.id, action: "power-on" },
      {
        onSuccess: (result) => {
          toast({
            tone: "info",
            title: t("servers.power.power-on.started", { name: server.name }),
          });
          onStarted(result.operation.id);
        },
        onError: (error) =>
          toast({
            tone: "danger",
            title:
              error instanceof ApiProblem && error.problem
                ? problemText(t, error.problem)
                : t("common.unexpected"),
          }),
      },
    );
  };
}

export function ServersPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const servers = useServers(projectId);
  const running = runningByServer(useRunningOperations(projectId).data);
  const { readOnly, reason: readOnlyReason, token: tokenReadOnly } = useReadOnly(projectId);
  // Guests may only look (docs/users.md): no create action; the API refuses them anyway.
  const canWrite = useCanWrite(projectId);
  const [text, setText] = useState("");
  const [status, setStatus] = useState("");
  const [power, setPower] = useState<{ server: Server; action: DisruptiveAction } | null>(null);
  const toasts = useOperationToasts(projectId);
  const powerOn = useRowPowerOn(projectId);
  // Managed appliances are their own kind (ADR 0002 A): hidden here unless asked for.
  const appliances = useAppliances(projectId).data;
  const [showManaged, setShowManaged] = useState(false);
  const managed = useMemo(
    () => new Map((appliances ?? []).map((a) => [a.serverId, a])),
    [appliances],
  );

  const rows = useMemo(
    () =>
      servers.data?.filter(
        (s) => (showManaged || !managed.has(s.id)) && matchesFilter(s, text, status),
      ),
    [servers.data, text, status, showManaged, managed],
  );
  const withPrivate = servers.data?.some((s) => s.privateIps.length > 0) ?? false;
  const open = (server: Server, tab?: "delete") =>
    void navigate({
      to: "/projects/$projectId/servers/$serverId",
      params: { projectId, serverId: server.id },
      ...(tab ? { search: { tab } } : {}),
    });

  const menu = (server: Server): MenuItem[][] => {
    const busy = running.has(server.id) || server.locked;
    const blocked = readOnly ? readOnlyReason : t("servers.busy");
    const disabled = readOnly || busy;
    const on = server.status === "running";
    return [
      [
        {
          label: t("servers.open"),
          icon: <ExternalLink className="size-4" aria-hidden />,
          onSelect: () => open(server),
        },
        {
          label: t("terminal.open"),
          icon: <SquareTerminal className="size-4" aria-hidden />,
          disabled: !canWrite || serverBlock(server) !== null,
          reason: !canWrite
            ? t("terminal.guest")
            : serverBlock(server) === "off"
              ? t("servers.notRunning")
              : t("terminal.picker.noAddress"),
          onSelect: () => terminals.openPicker({ projectId, server }),
        },
        {
          label: t("console.open"),
          icon: <MonitorPlay className="size-4" aria-hidden />,
          disabled: !canWrite,
          reason: t("terminal.guest"),
          onSelect: () =>
            void navigate({
              to: "/projects/$projectId/servers/$serverId/console",
              params: { projectId, serverId: server.id },
            }),
        },
      ],
      [
        on
          ? {
              label: t("servers.power.shutdown.action"),
              icon: <PowerOff className="size-4" aria-hidden />,
              disabled,
              reason: blocked,
              onSelect: () => setPower({ server, action: "shutdown" }),
            }
          : {
              label: t("servers.power.power-on.action"),
              icon: <Power className="size-4" aria-hidden />,
              disabled: disabled || server.status !== "off",
              reason: server.status !== "off" ? t("servers.notOff") : blocked,
              onSelect: () => powerOn(server, toasts.watch),
            },
        {
          label: t("servers.power.power-off.action"),
          icon: <PowerOff className="size-4" aria-hidden />,
          disabled: disabled || !on,
          reason: !on ? t("servers.notRunning") : blocked,
          onSelect: () => setPower({ server, action: "power-off" }),
        },
        {
          label: t("servers.power.reboot.action"),
          icon: <RotateCcw className="size-4" aria-hidden />,
          disabled: disabled || !on,
          reason: !on ? t("servers.notRunning") : blocked,
          onSelect: () => setPower({ server, action: "reboot" }),
        },
      ],
      [
        {
          label: t("servers.delete.action"),
          icon: <Trash2 className="size-4" aria-hidden />,
          danger: true,
          disabled: readOnly || managed.has(server.id),
          reason: managed.has(server.id) ? t("servers.appliance.onlyPackage") : readOnlyReason,
          onSelect: () => open(server, "delete"),
        },
      ],
    ];
  };

  const empty = (
    <EmptyState
      icon={<ServerIcon className="size-6" aria-hidden />}
      title={t("servers.empty")}
      action={
        canWrite ? <CreateServerButton projectId={projectId} disabled={readOnly} /> : undefined
      }
      help="servers"
    >
      {t("servers.emptyLead")}
    </EmptyState>
  );

  return (
    <>
      <PageHeader
        title={t("nav.servers")}
        intro={t("servers.intro")}
        help="servers"
        actions={
          servers.data?.length && canWrite ? (
            <CreateServerButton projectId={projectId} disabled={readOnly} />
          ) : undefined
        }
      />
      {tokenReadOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      {servers.data && servers.data.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("servers.filterPlaceholder")}
            aria-label={t("servers.filter")}
            className="min-h-9 w-full min-w-0 rounded-lg border border-line bg-surface px-3 text-sm text-text placeholder:text-muted focus:border-accent sm:w-72"
          />
          <Select
            label={t("servers.statusFilter")}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">{t("servers.anyStatus")}</option>
            {ServerStatus.options.map((s) => (
              <option key={s} value={s}>
                {t(`servers.status.${s}`)}
              </option>
            ))}
          </Select>
          {managed.size > 0 && (
            <label className="flex min-h-9 items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-accent"
                checked={showManaged}
                onChange={(e) => setShowManaged(e.target.checked)}
              />
              {t("servers.appliance.show", { count: managed.size })}
            </label>
          )}
        </div>
      )}
      {servers.error ? (
        <ErrorMessage error={servers.error} />
      ) : rows && rows.length === 0 && servers.data && servers.data.length > 0 ? (
        <p className="rounded-xl border border-dashed border-line bg-surface px-4 py-8 text-center text-sm text-muted">
          {t("servers.noMatch")}
        </p>
      ) : (
        <DataTable<Server>
          label={t("nav.servers")}
          rows={rows}
          rowKey={(s) => s.id}
          initialSort={{ key: "name", dir: "asc" }}
          empty={empty}
          progress={(s) => running.get(s.id)?.progress ?? null}
          muted={(s) => s.status === "deleting"}
          columns={[
            {
              key: "name",
              header: t("servers.name"),
              primary: true,
              sort: (s) => s.name.toLowerCase(),
              cell: (s) => {
                const operation = running.get(s.id);
                return (
                  <div className="flex min-w-0 items-center gap-2.5">
                    <OsIcon
                      flavor={s.image?.osFlavor}
                      version={s.image?.osVersion}
                      description={s.image?.description}
                      variant="brand"
                      className="size-5"
                    />
                    <div className="flex min-w-0 flex-col">
                      <span className="flex min-w-0 items-center gap-2">
                        <Link
                          to="/projects/$projectId/servers/$serverId"
                          params={{ projectId, serverId: s.id }}
                          className="truncate font-medium hover:text-accent hover:underline"
                          title={s.name}
                        >
                          {s.name}
                        </Link>
                        {managed.has(s.id) && <Badge>{t("servers.appliance.badge")}</Badge>}
                      </span>
                      <span className="truncate text-xs text-muted">
                        {operation ? operationText(operation, t) : specLine(s, t)}
                      </span>
                    </div>
                  </div>
                );
              },
            },
            {
              key: "status",
              header: t("servers.statusColumn"),
              sort: (s) => s.status,
              cell: (s) => <StatusLabel status={s.status} />,
            },
            {
              key: "ip",
              header: t("servers.publicIp"),
              sort: (s) => s.publicIpv4?.address ?? s.publicIpv6?.network ?? "",
              cell: (s) => (
                <div className="flex min-w-0 flex-col">
                  {s.publicIpv4 ? (
                    <span className="truncate font-mono text-xs">{s.publicIpv4.address}</span>
                  ) : (
                    <span className="truncate text-xs text-muted">{t("servers.noIpv4")}</span>
                  )}
                  {s.publicIpv6 && (
                    <span className="truncate font-mono text-xs text-muted">
                      {s.publicIpv6.network}
                    </span>
                  )}
                </div>
              ),
            },
            ...(withPrivate
              ? [
                  {
                    key: "private",
                    header: t("servers.privateIp"),
                    hideOnPhone: true,
                    cell: (s: Server) => (
                      <span className="font-mono text-xs">
                        {s.privateIps.map((p) => p.ip).join(", ") || "—"}
                      </span>
                    ),
                  },
                ]
              : []),
            {
              key: "region",
              header: t("servers.region"),
              hideOnPhone: true,
              sort: (s) => s.region,
              cell: (s) => <span className="text-muted">{s.region}</span>,
            },
            {
              key: "created",
              header: t("servers.created"),
              hideOnPhone: true,
              sort: (s) => s.createdAt,
              cell: (s) => (
                <span className="text-muted">{relativeTime(s.createdAt, i18n.language)}</span>
              ),
            },
          ]}
          rowMenu={menu}
        />
      )}
      {power && (
        <PowerDialog
          projectId={projectId}
          server={power.server}
          action={power.action}
          onClose={() => setPower(null)}
          onStarted={toasts.watch}
        />
      )}
      {toasts.element}
    </>
  );
}
